import { ROUTES } from '../../../constants/RouteNameMapping';

/** Pattern that matches markdown links using the `hide-link:` scheme, e.g. `[Search Results](hide-link:/sourcing/search/abc)` */
const HIDE_LINK_PATTERN = /\[([^\]]+)\]\(hide-link:([^)]+)\)/g;

const SOURCING_SEARCH_PREFIX = '/sourcing/search/';

/**
 * Maps a backend hide-link path to the corresponding internal app route.
 * Only paths starting with the known sourcing search prefix are transformed;
 * all other paths are returned as-is so future hide-link types are not misrouted.
 */
export function resolveHideLinkPath(path: string): string {
  if (path.startsWith(SOURCING_SEARCH_PREFIX)) {
    const id = path.slice(SOURCING_SEARCH_PREFIX.length);
    if (id) {
      return `/${ROUTES.SOURCING}/${ROUTES.SOURCING_RESULTS}/${id}`;
    }
  }
  return path;
}

export interface HideLink {
  readonly label: string;
  readonly path: string;
}

export interface ParsedMessageContent {
  readonly cleanContent: string;
  readonly hideLinks: readonly HideLink[];
}

/**
 * The server may store message content either as plain markdown (emitted during streaming) or
 * as a JSON-encoded content array: `[{"type":"text","text":"..."}]`.
 * This function normalises both into a plain markdown string.
 */
export function parseJsonContent(content: string): string {
  try {
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed)) {
      return parsed
        .filter((part): part is { type: string; text: string } => part?.type === 'text')
        .map((part) => part.text)
        .join('');
    }
  } catch {
    // not JSON – fall through
  }
  return content;
}

/**
 * Extracts `hide-link` markdown anchors from `text`, returning the text with those anchors
 * removed (along with any immediately preceding whitespace / separator lines) and a list of
 * the discovered links.
 */
export function extractHideLinks(text: string): ParsedMessageContent {
  const hideLinks: HideLink[] = [];

  const cleanContent = text
    .replace(HIDE_LINK_PATTERN, (_match, label: string, path: string) => {
      hideLinks.push({ label, path });
      return '';
    })
    .trimEnd();

  return { cleanContent, hideLinks };
}

/**
 * Convenience function that first normalises JSON-encoded content and then strips hide-links.
 */
export function parseMessageContent(content: string): ParsedMessageContent {
  const normalised = parseJsonContent(content);
  return extractHideLinks(normalised);
}
